<script setup lang="ts">
import { useLanguage } from '#composables/useLanguage';
import { formatIndicatorValue, getNumberOrDefault } from '#helpers';
import type { Indicator } from '@carrefour-gcs/shared';
import type { EChartsOption, PieSeriesOption } from 'echarts';
import { PieChart } from 'echarts/charts';
import { LegendComponent, TooltipComponent } from 'echarts/components';
import { registerTheme, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { computed, onBeforeMount } from 'vue';
import VChart from 'vue-echarts';
import { LINKS_GROWTH_THEME } from './constants';

const props = defineProps<{
  ordinates: NonNullable<Indicator['ordinates']>;
}>();

const { t, language } = useLanguage();

onBeforeMount(() => {
  use([CanvasRenderer, PieChart, TooltipComponent, LegendComponent]);
  registerTheme('linksGrowth', LINKS_GROWTH_THEME);
});

const option = computed(() => {
  const legend = props.ordinates.map((value) => t(value.name));
  const unit = props.ordinates[0]?.unit ?? 'integer';
  const unitLabel = props.ordinates[0]?.unitLabel;
  const data = props.ordinates
    .filter((ordinate) => ordinate.value && ordinate.name !== undefined)
    .map<PieSeriesOption>((ordinate) => ({
      name: t(ordinate.name),
      value: getNumberOrDefault(ordinate.value, 0)
    }));

  return {
    tooltip: {
      trigger: 'item',
      valueFormatter: (value: number) =>
        formatIndicatorValue({
          indicator: { value, unit, unitLabel },
          language: language.value
        })
    },
    legend: {
      left: 0,
      bottom: 0,
      orient: 'vertical',
      data: legend
    },
    series: {
      type: 'pie',
      left: '100%',
      top: 85,
      right: 85,
      radius: 85,
      center: [0, 0],
      label: { show: true, formatter: '{d}%', position: 'inside' },
      emphasis: { scale: false },
      data
    }
  } as EChartsOption;
});
</script>

<template>
  <VChart class="w-full h-44" :option="option" theme="linksGrowth" autoresize />
</template>
